import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import PageTitle from '../../../layouts/PageTitle';
import * as api from '../../../../services/api/projectsApi';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import * as S from '../../Common/internalToolsStyles';

const PROJECT_TYPES = [
  { value: 'client', label: 'Klientprojekt', desc: 'Debiterbart arbete för en kund' },
  { value: 'internal', label: 'Internt', desc: 'Internt arbete, ej debiterbart' },
  { value: 'station', label: 'Station', desc: 'Restaurang, bar, kök' },
  { value: 'site', label: 'Arbetsplats', desc: 'Byggarbetsplats' },
  { value: 'job', label: 'Uppdrag', desc: 'Städuppdrag, jobb' },
];

const BILLING_MODELS = [
  { value: 'time_materials', label: 'Tid & Material', desc: 'Fakturera arbetade timmar' },
  { value: 'fixed_price', label: 'Fastpris', desc: 'Fast kontraktssumma' },
  { value: 'retainer', label: 'Retainer', desc: 'Förbetald timbank' },
  { value: 'non_billable', label: 'Ej debiterbar', desc: 'Intern tid, ingen faktura' },
];

const COLORS = ['#3b82f6', '#10b981', '#f97316', '#ef4444', '#a855f7', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1', '#78716c'];

const validationSchema = Yup.object({
  name: Yup.string().max(120, 'Max 120 tecken').required('Projektnamn krävs'),
  code: Yup.string().max(20, 'Max 20 tecken').nullable(),
  projectType: Yup.string().oneOf(['client', 'internal', 'station', 'site', 'job']).required('Typ krävs'),
  billingModel: Yup.string().oneOf(['time_materials', 'fixed_price', 'retainer', 'non_billable']).required('Debiteringsmodell krävs'),
  customerId: Yup.string().nullable(),
  hourlyRate: Yup.number().min(0, 'Timpriset kan inte vara negativt').nullable(),
  budgetHours: Yup.number().min(0).nullable(),
  fixedPriceAmount: Yup.number().min(0).nullable(),
  description: Yup.string().nullable(),
});

const ProjectEditPage = () => {
  const { companyId, projectId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const isSetup = new URLSearchParams(location.search).get('setup') === '1';

  const [project, setProject] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [projRes, custRes, artRes] = await Promise.all([
          api.getProject(companyId, projectId),
          axios.get(getApiUrl(`${companyId}/sales/customers`), { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(getApiUrl(`${companyId}/articles`), { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setProject(projRes.data.project);
        const custData = Array.isArray(custRes.data) ? custRes.data : custRes.data.data || [];
        setCustomers(custData);
        const artData = Array.isArray(artRes.data) ? artRes.data : artRes.data.data || [];
        setArticles(artData.filter(a => a.type === 'Service' || a.unit === 'Hour'));
      } catch (err) {
        if (!cancelled) setLoadError(err.response?.data?.message || 'Kunde inte ladda projektet');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, projectId]);

  const initialValues = useMemo(() => ({
    name: project?.name || '',
    code: project?.code || '',
    description: project?.description || '',
    color: project?.color || COLORS[0],
    projectType: project?.project_type || 'client',
    billingModel: project?.billing_model || 'time_materials',
    customerId: project?.customer_id || '',
    clientReference: project?.client_reference || '',
    articleId: project?.article_id || '',
    hourlyRate: project?.hourly_rate || '',
    budgetHours: project?.budget_hours || '',
    fixedPriceAmount: project?.fixed_price_amount || '',
    currency: project?.currency || 'SEK',
    requireMembership: project?.require_membership !== undefined ? !!project.require_membership : true,
  }), [project]);

  // Missing fields surface the incomplete-stub story on a setup-landing so
  // the user knows *what* is left, not just that "something" is.
  const missingFields = useMemo(() => {
    if (!project) return [];
    const m = [];
    if (project.billing_model === 'time_materials' && !project.hourly_rate) m.push('Timpris');
    if (project.project_type === 'client' && !project.customer_id) m.push('Kund');
    if (!project.code) m.push('Projektkod');
    if (!project.budget_hours && project.billing_model !== 'non_billable') m.push('Budget');
    return m;
  }, [project]);

  const handleSubmit = async (values) => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.updateProject(companyId, projectId, {
        name: values.name,
        code: values.code || null,
        description: values.description || null,
        color: values.color || null,
        projectType: values.projectType,
        billingModel: values.billingModel,
        customerId: values.customerId || null,
        clientReference: values.clientReference || null,
        articleId: values.articleId || null,
        hourlyRate: values.hourlyRate ? parseFloat(values.hourlyRate) : null,
        budgetHours: values.budgetHours ? parseFloat(values.budgetHours) : null,
        fixedPriceAmount: values.fixedPriceAmount ? parseFloat(values.fixedPriceAmount) : null,
        currency: values.currency,
        requireMembership: values.requireMembership,
      });
      history.push(`/${companyId}/projects/${projectId}`);
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Kunde inte spara projektet');
      // Scroll the error into view for long forms where the alert would
      // otherwise render above the current scroll position.
      setTimeout(() => {
        document.querySelector('.proj-edit-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (loadError || !project) {
    return (
      <>
        <PageTitle activeMenu="Redigera" motherMenu="Projekt" />
        <Alert variant="danger">{loadError || 'Projektet hittades inte'}</Alert>
        <button onClick={() => history.push(`/${companyId}/projects`)} style={S.btnSecondary}>
          <ArrowLeft size={14} /> Tillbaka till projektlistan
        </button>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Redigera" motherMenu="Projekt" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formikSubmit, setFieldValue }) => (
          <form onSubmit={formikSubmit}>

            {/* Toolbar — back link left, title center, save actions right. */}
            <div className="proj-toolbar proj-edit-toolbar">
              <button
                type="button"
                onClick={() => history.push(`/${companyId}/projects/${projectId}`)}
                style={S.btnSecondary}
              >
                <ArrowLeft size={14} /> Tillbaka
              </button>
              <div className="proj-edit-titleblock">
                <div className="proj-edit-title">Redigera projekt</div>
              </div>
              <div className="proj-toolbar-right">
                <button
                  type="button"
                  onClick={() => history.push(`/${companyId}/projects/${projectId}`)}
                  disabled={saving}
                  style={S.btnSecondary}
                >
                  Avbryt
                </button>
                <button type="submit" disabled={saving} style={S.btnPrimary}>
                  {saving ? <Loader2 size={14} className="proj-spin" /> : <Save size={14} />}
                  Spara ändringar
                </button>
              </div>
            </div>

            {/* Setup banner — surface what's missing when the user just
                finished quick-create so they know which fields to fill. */}
            {isSetup && (
              <div className="proj-setup-banner">
                <div>
                  <div className="proj-setup-banner-title">Slutför inställningar</div>
                  <div className="proj-setup-banner-desc">
                    {missingFields.length > 0
                      ? <>Fyll i <b>{missingFields.join(', ')}</b> så att projektet är redo för tidrapportering och fakturering.</>
                      : 'Komplettera fälten nedan så att projektet är redo att användas.'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => history.push(`/${companyId}/projects/${projectId}`)}
                  className="proj-setup-skip"
                >
                  Hoppa över
                </button>
              </div>
            )}

            {saveError && (
              <Alert variant="danger" dismissible onClose={() => setSaveError(null)} className="proj-edit-error">
                {saveError}
              </Alert>
            )}

            <div style={S.card}>
              <div style={{ padding: '24px 28px' }}>

                {/* Grundläggande information */}
                <div style={S.text.sectionHeader}>Grundläggande information</div>
                <div className="proj-form-grid" style={{ marginTop: 12, marginBottom: 28 }}>
                  <div style={{ gridColumn: 'span 8' }}>
                    <label className="proj-label">
                      Projektnamn <span className="proj-required">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="t.ex. Website Redesign"
                      className={`proj-input ${touched.name && errors.name ? 'is-invalid' : ''}`}
                    />
                    {touched.name && errors.name && <div className="proj-error-msg">{errors.name}</div>}
                  </div>
                  <div style={{ gridColumn: 'span 4' }}>
                    <label className="proj-label">Projektkod</label>
                    <input
                      type="text"
                      name="code"
                      value={values.code}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="t.ex. ACME-WEB"
                      className={`proj-input ${touched.code && errors.code ? 'is-invalid' : ''}`}
                    />
                    {touched.code && errors.code && <div className="proj-error-msg">{errors.code}</div>}
                  </div>
                  <div style={{ gridColumn: 'span 12' }}>
                    <label className="proj-label">Beskrivning</label>
                    <textarea
                      rows={2}
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      placeholder="Kort beskrivning av projektet..."
                      className="proj-input proj-textarea"
                    />
                  </div>
                  <div style={{ gridColumn: 'span 12' }}>
                    <label className="proj-label">Färg</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {COLORS.map(c => {
                        const selected = values.color === c;
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setFieldValue('color', c)}
                            aria-label={`Färg ${c}`}
                            className={`proj-color-swatch ${selected ? 'selected' : ''}`}
                            style={{ backgroundColor: c }}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Projekttyp */}
                <div style={S.text.sectionHeader}>Projekttyp</div>
                <div className="proj-option-grid" style={{ marginTop: 12, marginBottom: 28 }}>
                  {PROJECT_TYPES.map(pt => {
                    const selected = values.projectType === pt.value;
                    return (
                      <div
                        key={pt.value}
                        role="button"
                        tabIndex={0}
                        onClick={() => setFieldValue('projectType', pt.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFieldValue('projectType', pt.value); } }}
                        className={`proj-option ${selected ? 'selected' : ''}`}
                      >
                        <div className="proj-option-label">{pt.label}</div>
                        <div className="proj-option-desc">{pt.desc}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Debitering */}
                <div style={S.text.sectionHeader}>Debitering</div>
                <div className="proj-option-grid proj-billing-grid" style={{ marginTop: 12, marginBottom: 20 }}>
                  {BILLING_MODELS.map(bm => {
                    const selected = values.billingModel === bm.value;
                    return (
                      <div
                        key={bm.value}
                        role="button"
                        tabIndex={0}
                        onClick={() => setFieldValue('billingModel', bm.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFieldValue('billingModel', bm.value); } }}
                        className={`proj-option ${selected ? 'selected' : ''}`}
                      >
                        <div className="proj-option-label">{bm.label}</div>
                        <div className="proj-option-desc">{bm.desc}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Client row — whole row toggles with project type, so no
                    mid-grid reflow. */}
                {values.projectType === 'client' && (
                  <div className="proj-form-grid" style={{ marginBottom: 16 }}>
                    <div style={{ gridColumn: 'span 6' }}>
                      <label className="proj-label">Kund</label>
                      <Select
                        options={customers.map(c => ({
                          value: c.id || c.customer_id,
                          label: `${c.name}${c.orgnumber ? ` (${c.orgnumber})` : ''}`,
                        }))}
                        value={values.customerId ? {
                          value: values.customerId,
                          label: (() => {
                            const c = customers.find(c => (c.id || c.customer_id) === values.customerId);
                            return c ? `${c.name}${c.orgnumber ? ` (${c.orgnumber})` : ''}` : values.customerId;
                          })(),
                        } : null}
                        onChange={(option) => setFieldValue('customerId', option ? option.value : '')}
                        isClearable
                        isSearchable
                        placeholder="Sök eller välj kund..."
                        noOptionsMessage={() => 'Inga kunder hittades'}
                        styles={S.selectStyles}
                      />
                    </div>
                    <div style={{ gridColumn: 'span 6' }}>
                      <label className="proj-label">Kundreferens</label>
                      <input
                        type="text"
                        name="clientReference"
                        value={values.clientReference}
                        onChange={handleChange}
                        placeholder="PO-nummer, avtalsref..."
                        className="proj-input"
                      />
                    </div>
                  </div>
                )}

                {/* Pricing sub-card — contents swap based on billing model,
                    but the sub-card itself is always rendered so the grid
                    below doesn't reflow when the user toggles between
                    billing models. */}
                <div className="proj-pricing-card" style={{ marginBottom: 16 }}>
                  {values.billingModel === 'non_billable' ? (
                    <div className="proj-pricing-empty">
                      Interna projekt faktureras inte — inget debiteringspris behövs.
                    </div>
                  ) : (
                    <div className="proj-form-grid">
                      <div style={{ gridColumn: values.billingModel === 'fixed_price' ? 'span 6' : 'span 12' }}>
                        <label className="proj-label">
                          Timpris ({values.currency}/h)
                          {values.billingModel === 'time_materials' && <span className="proj-required"> *</span>}
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <input
                            type="number"
                            name="hourlyRate"
                            value={values.hourlyRate}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="t.ex. 950"
                            min={0}
                            step="any"
                            className={`proj-input ${touched.hourlyRate && errors.hourlyRate ? 'is-invalid' : ''}`}
                            style={{ maxWidth: 200 }}
                          />
                          <span style={S.text.caption}>{values.currency}/timme</span>
                          {values.hourlyRate > 0 && values.budgetHours > 0 && (
                            <span style={{ ...S.text.caption, color: S.colors.textMuted }}>
                              = {(values.hourlyRate * values.budgetHours).toLocaleString('sv-SE')} {values.currency} vid full budget
                            </span>
                          )}
                        </div>
                        {touched.hourlyRate && errors.hourlyRate && <div className="proj-error-msg">{errors.hourlyRate}</div>}
                        <div className="proj-helper">
                          Standardpris per timme. Specifika priser per person eller roll läggs till i projektinställningarna.
                        </div>
                      </div>

                      {values.billingModel === 'fixed_price' && (
                        <div style={{ gridColumn: 'span 6' }}>
                          <label className="proj-label">Fastpris ({values.currency})</label>
                          <input
                            type="number"
                            name="fixedPriceAmount"
                            value={values.fixedPriceAmount}
                            onChange={handleChange}
                            placeholder="0"
                            min={0}
                            className="proj-input"
                          />
                          <div className="proj-helper">
                            Fast kontraktssumma — faktureras oavsett arbetade timmar.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Budget + article + currency — always rendered in a stable
                    3-column grid. Article is disabled for non_billable. */}
                <div className="proj-form-grid" style={{ marginBottom: 28 }}>
                  <div style={{ gridColumn: 'span 4' }}>
                    <label className="proj-label">Budgeterade timmar</label>
                    <input
                      type="number"
                      name="budgetHours"
                      value={values.budgetHours}
                      onChange={handleChange}
                      placeholder="0"
                      min={0}
                      className="proj-input"
                    />
                  </div>
                  <div style={{ gridColumn: 'span 4' }}>
                    <label className="proj-label">Valuta</label>
                    <select
                      name="currency"
                      value={values.currency}
                      onChange={handleChange}
                      className="proj-input proj-select"
                    >
                      <option value="SEK">SEK</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: 'span 4' }}>
                    <label className="proj-label">Faktura-artikel</label>
                    <Select
                      isDisabled={values.billingModel === 'non_billable'}
                      options={articles.map(a => ({
                        value: a.article_id || a.id,
                        label: `${a.description || a.productcode}${a.productcode ? ` (${a.productcode})` : ''}`,
                      }))}
                      value={values.articleId ? {
                        value: values.articleId,
                        label: (() => {
                          const a = articles.find(a => (a.article_id || a.id) === values.articleId);
                          return a ? `${a.description || a.productcode}` : values.articleId;
                        })(),
                      } : null}
                      onChange={(option) => setFieldValue('articleId', option ? option.value : '')}
                      isClearable
                      isSearchable
                      placeholder={values.billingModel === 'non_billable' ? 'Ej tillämpligt' : 'Välj artikel...'}
                      noOptionsMessage={() => 'Inga tjänsteartiklar hittades'}
                      styles={S.selectStyles}
                    />
                    <div className="proj-helper">
                      Används på fakturarader. Moms alltid 25% för tjänster.
                    </div>
                  </div>
                </div>

                {/* Åtkomst */}
                <div style={S.text.sectionHeader}>Åtkomst</div>
                <div style={{ marginTop: 12 }}>
                  <div className="proj-segmented">
                    <button
                      type="button"
                      onClick={() => setFieldValue('requireMembership', true)}
                      className={`proj-segmented-opt ${values.requireMembership ? 'active' : ''}`}
                    >
                      Endast medlemmar
                    </button>
                    <button
                      type="button"
                      onClick={() => setFieldValue('requireMembership', false)}
                      className={`proj-segmented-opt ${!values.requireMembership ? 'active' : ''}`}
                    >
                      Alla anställda
                    </button>
                  </div>
                  <div className="proj-helper" style={{ marginTop: 8 }}>
                    {values.requireMembership
                      ? 'Bara tilldelade medlemmar kan rapportera tid på det här projektet.'
                      : 'Alla anställda kan rapportera tid på det här projektet.'}
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>

      <style>{`
        /* Toolbar — matches ProjectReportsPage.proj-toolbar */
        .proj-toolbar {
          display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
          padding: 10px 14px; background: white;
          border: 1px solid ${S.colors.borderDefault}; border-radius: 10px;
          flex-wrap: wrap;
        }
        .proj-toolbar-right { display: flex; align-items: center; gap: 8px; margin-left: auto; }

        .proj-edit-toolbar { position: sticky; top: 0; z-index: 20; }
        .proj-edit-titleblock { display: flex; flex-direction: column; min-width: 0; }
        .proj-edit-title { font-size: 14px; font-weight: 600; color: ${S.colors.textPrimary}; line-height: 1.2; }
        .proj-edit-subtitle { font-size: 12px; color: ${S.colors.textLight}; line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        /* Setup banner */
        .proj-setup-banner {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
          padding: 12px 16px; margin-bottom: 12px;
          background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px;
        }
        .proj-setup-banner-title {
          font-size: 13px; font-weight: 600; color: #92400e; margin-bottom: 2px;
        }
        .proj-setup-banner-desc { font-size: 13px; color: #78350f; line-height: 1.5; }
        .proj-setup-banner-desc b { font-weight: 600; }
        .proj-setup-skip {
          background: transparent; border: none; color: #92400e;
          font-size: 12px; font-weight: 500; cursor: pointer; padding: 4px 8px;
          white-space: nowrap;
        }
        .proj-setup-skip:hover { text-decoration: underline; }

        /* Form grid — CSS grid, no Bootstrap Row/Col */
        .proj-form-grid {
          display: grid; grid-template-columns: repeat(12, 1fr); gap: 16px;
        }
        @media (max-width: 768px) {
          .proj-form-grid > * { grid-column: span 12 !important; }
        }

        /* Labels */
        .proj-label {
          display: block; margin-bottom: 6px;
          font-size: 12px; font-weight: 500; color: ${S.colors.textLight};
        }
        .proj-required { color: ${S.colors.danger}; font-weight: 600; }
        .proj-helper {
          font-size: 11px; color: ${S.colors.textLight}; margin-top: 6px; line-height: 1.4;
        }
        .proj-error-msg {
          font-size: 11px; color: ${S.colors.danger}; margin-top: 4px;
        }

        /* Pricing sub-card — swaps content based on billing model while
           keeping the surrounding grid stable. */
        .proj-pricing-card {
          padding: 14px 16px;
          background: ${S.colors.bgSubtle};
          border: 1px solid ${S.colors.borderLight};
          border-radius: 10px;
        }
        .proj-pricing-empty {
          font-size: 13px; color: ${S.colors.textMuted};
          padding: 4px 0;
        }

        /* Save-button spinner */
        @keyframes proj-spin { to { transform: rotate(360deg); } }
        .proj-spin { animation: proj-spin 0.8s linear infinite; }

        /* Inputs — tokenized, consistent with S.input */
        .proj-input {
          width: 100%;
          border-radius: 8px;
          border: 1px solid ${S.colors.borderDefault};
          font-size: 14px;
          height: 40px;
          padding: 0 12px;
          background: white;
          color: ${S.colors.textPrimary};
          transition: border-color 150ms ease, box-shadow 150ms ease;
          outline: none;
          font-family: inherit;
        }
        .proj-input:hover { border-color: #d1d5db; }
        .proj-input:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(68,129,78,0.12);
        }
        .proj-input.is-invalid {
          border-color: ${S.colors.danger};
        }
        .proj-input.is-invalid:focus {
          box-shadow: 0 0 0 3px ${S.colors.dangerLight};
        }
        .proj-textarea {
          height: auto;
          padding: 10px 12px;
          resize: vertical;
          line-height: 1.5;
          min-height: 60px;
        }
        .proj-select {
          appearance: none;
          -webkit-appearance: none;
          padding-right: 32px;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
          background-repeat: no-repeat;
          background-position: right 12px center;
        }

        /* Color swatch */
        .proj-color-swatch {
          width: 28px; height: 28px; border-radius: 50%;
          border: 2px solid transparent;
          cursor: pointer; transition: transform 120ms ease;
          padding: 0;
        }
        .proj-color-swatch.selected {
          outline: 2px solid var(--primary);
          outline-offset: 2px;
          transform: scale(1.1);
        }
        .proj-color-swatch:focus-visible {
          outline: 2px solid var(--primary);
          outline-offset: 2px;
        }

        /* Option tiles — project type + billing model grids */
        .proj-option-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
        }
        .proj-option-grid.proj-billing-grid {
          grid-template-columns: repeat(4, 1fr);
        }
        @media (max-width: 640px) {
          .proj-option-grid, .proj-option-grid.proj-billing-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        .proj-option {
          padding: 12px;
          border-radius: 8px;
          border: 1px solid ${S.colors.borderDefault};
          background: white;
          cursor: pointer;
          transition: background 120ms ease, border-color 120ms ease;
        }
        .proj-option:hover { background: ${S.colors.bgHover}; }
        .proj-option.selected {
          border-color: var(--primary);
          background: var(--rgba-primary-1);
        }
        .proj-option-label {
          font-size: 13px; font-weight: 500; color: ${S.colors.textPrimary}; margin-bottom: 2px;
        }
        .proj-option-desc {
          font-size: 11px; color: ${S.colors.textLight}; line-height: 1.4;
        }
        .proj-option:focus-visible {
          outline: 2px solid var(--primary); outline-offset: 2px;
        }

        /* Segmented control for membership */
        .proj-segmented {
          display: inline-flex; border: 1px solid ${S.colors.borderDefault};
          border-radius: 8px; padding: 3px; background: ${S.colors.bgSubtle};
        }
        .proj-segmented-opt {
          background: transparent; border: none;
          padding: 6px 14px; border-radius: 6px;
          font-size: 13px; font-weight: 500; color: ${S.colors.textMuted};
          cursor: pointer; transition: all 120ms ease;
        }
        .proj-segmented-opt:hover { color: ${S.colors.textPrimary}; }
        .proj-segmented-opt.active {
          background: white; color: ${S.colors.textPrimary};
          box-shadow: 0 1px 2px rgba(0,0,0,0.06);
        }
      `}</style>
    </>
  );
};

export default ProjectEditPage;
